import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { SetWalletSetupModals } from "@/constants/enums";
import { useWeb3 } from "@/context/Web3Context";
import { Dispatch, SetStateAction, useEffect } from "react";
import { useTranslation } from "react-i18next";
import myronLogo from "@/assets/MYRON-icon-transparent.webp";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/opacity.css";
import { FramerButton } from "@/components/shared";

type Props = {
  showModal: SetWalletSetupModals;
  setShowModal: Dispatch<SetStateAction<SetWalletSetupModals>>;
}

const steps = [
  "stepone",
  "steptwo",
  "stepthree",
  "stepfour",
  "stepfive",
  "stepsix"
];

function SetWalletSetupModal({ showModal, setShowModal }: Props) {

  // Hooks
  const { isConnected, connectWallet } = useWeb3();
  const { t } = useTranslation(["modals"]);

  useEffect(() => {
    if(isConnected) {
      setShowModal(SetWalletSetupModals.PublicKey);
    }
  }, [isConnected]);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setShowModal(SetWalletSetupModals.NoModal);
    }
  };

  return (
    <Dialog open={showModal === SetWalletSetupModals.Setup} onOpenChange={handleOpenChange}>
      <DialogContent className="px-6 max-w-3xl max-h-[90vh] overflow-y-auto dark:bg-mfdarklight"
        onPointerDownOutside={(event) => {
          event.preventDefault();
        }}
      >
        <DialogHeader className="sr-only">
          <DialogTitle>Wallet Setup</DialogTitle>
        </DialogHeader>

        <div className="flex-1 pt-2 overflow-y-auto">
          {/* Image container */}
          <div className="flex justify-center mb-8 py-8">
            <LazyLoadImage
              alt={"Myron logo"}
              className="h-16"
              src={myronLogo}
              effect="opacity"
            />
          </div>

          {/* Content container */}
          <div className="mb-4">
            <p className="mb-2 font-semibold">{t("initialisepublickey.setup.title")}</p>
            <ol className="list-decimal ml-8">
              {steps.map((step) => (
                <li key={step} className="mb-1">
                  <p className="font-semibold">{t(`initialisepublickey.setup.${step}title`)}</p>{t(`initialisepublickey.setup.${step}text`)}
                </li>
              ))}
            </ol>
          </div>

          {/* Warning container */}
          <div className="p-4 rounded-md bg-mfpurple text-white mb-4">
            <p>
              <span className="font-bold">{t("initialisepublickey.setup.notetitle")}:&nbsp;</span>
              {t("initialisepublickey.setup.note")}
            </p>
          </div>

          {/* Support container */}
          <div className="mb-4">
            <p>{t("initialisepublickey.setup.support")} <a href="mailto:contact@myron.finance" className="text-blue-400">contact@myron.finance</a></p>
          </div>

          {/* Buttons container */}
          <div className="flex justify-between">
            <FramerButton
              onClick={() => setShowModal(SetWalletSetupModals.Welcome)}
              variant="outline-primary"
            >
              {t("common:buttons.back")}
            </FramerButton>
            <FramerButton
              onClick={connectWallet}
              className="bg-mforange text-white hover:bg-mforange/80"
            >
              {t("common:buttons.connect")}
            </FramerButton>
          </div>

          {/* Stepper */}
          <div className="flex justify-center mt-8">
            <div className="flex items-center">
              <div className="h-2 w-2 bg-gray-300 rounded-full" />
              <div className="h-2 w-2 bg-mforange rounded-full mx-2" />
              <div className="h-2 w-2 bg-gray-300 rounded-full" />
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default SetWalletSetupModal;
